import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { LuPlus, LuTrash2 } from "react-icons/lu";
import Button from "../../components/button";
import useRequest from "../../components/hooks/use-request";
import { showToast } from "../../components/toast";
import { CircleLoader } from "react-spinners";
import Icon from "../../assets/icons";
import { toNumber, formatCount } from "../../utilities/functions";

const CURRENCIES = ["NGN", "USD", "CNY"] as const;
type Currency = (typeof CURRENCIES)[number];
type FeeType = "fixed" | "percentage";
type LimitType = "one_time" | "daily";

// Tiered pricing/withdrawal fee types (proactive, richer model)
type TierFeeType = "fixed" | "percentage" | "percentage_plus_fixed";
const TIERED_CURRENCY = "NGN"; // NGN only for now; payload keeps currency for forward-compat

interface FeeFormData {
  feeValue: string;
  feeType: FeeType;
  feeCurrency: Currency;
  setAsDefault: boolean;
}

interface LimitFormData {
  limitValue: string;
  limitCurrency: Currency;
  limitType: LimitType;
}

interface TierState {
  id: number;
  from: string; // editable lower bound (ignored for the first tier — bound to min)
  feeType: TierFeeType;
  percentage: string;
  fixed: string;
  maxFee: string; // "" = uncapped
}

const TIER_FEE_TYPES: { value: TierFeeType; label: string }[] = [
  { value: "percentage", label: "Percentage (%)" },
  { value: "fixed", label: "Fixed (₦)" },
  { value: "percentage_plus_fixed", label: "Percentage + Fixed" },
];

// Per-currency pill colours, reused across both the fees and limits lists
const currencyStyles: Record<string, string> = {
  NGN: "bg-blue-50 text-[#0979A1] border-[#0979A1]",
  USD: "bg-green-50 text-green-700 border-green-600",
  CNY: "bg-purple-50 text-purple-700 border-purple-600",
};
const currencyStyle = (c: string) =>
  currencyStyles[c] || "bg-gray-50 text-gray-700 border-gray-400";

const limitTypeLabel = (t: string) => (t === "daily" ? "Daily" : "One-Time");

// Custom chevron so we control the gap after it (native selects pin the arrow
// to the edge). Positioned 0.75rem from the right, giving padding after it.
const selectChevron: React.CSSProperties = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23040821' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 0.75rem center",
  backgroundSize: "0.9rem",
};

// Format a raw numeric string with thousands separators while preserving a
// decimal portion the user is still typing (e.g. "3500." or "3500.5").
const formatAmountInput = (val: string | number) => {
  if (val === "" || val == null) return "";
  const s = String(val);
  const dotIndex = s.indexOf(".");
  if (dotIndex === -1) {
    return s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  const intPart = s.slice(0, dotIndex).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${intPart}.${s.slice(dotIndex + 1)}`;
};

// Controlled comma-formatting amount input (no stuck leading zero) — RHF variant
const AmountInput = ({ field, placeholder, className }: any) => (
  <input
    ref={field.ref}
    name={field.name}
    onBlur={field.onBlur}
    type="text"
    inputMode="decimal"
    placeholder={placeholder}
    value={formatAmountInput(field.value)}
    onChange={(e) => {
      let raw = e.target.value.replace(/,/g, "");
      if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
      // Drop leading zeros ("02434" -> "2434") but keep a single 0 before a
      // decimal point (e.g. "0.5")
      raw = raw.replace(/^0+(?=\d)/, "");
      field.onChange(raw);
    }}
    className={className}
  />
);

// Plain (non-RHF) controlled variant used by the tiered sections
const PlainAmountInput = ({ value, onChange, placeholder, className, readOnly }: any) => (
  <input
    type="text"
    inputMode="decimal"
    readOnly={readOnly}
    placeholder={placeholder}
    value={formatAmountInput(value)}
    onChange={(e) => {
      let raw = e.target.value.replace(/,/g, "");
      if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
      raw = raw.replace(/^0+(?=\d)/, "");
      onChange(raw);
    }}
    className={className}
  />
);

const inputCls =
  "w-full h-[42px] px-3 border-2 rounded-md text-[14px] outline-none focus:border-[#0979A1]";
const roInputCls =
  "w-full h-[42px] px-3 border-2 rounded-md text-[14px] outline-none bg-gray-50 text-gray-500";
const labelCls = "text-[13px] font-medium text-[#040821] mb-1.5 block";

// ── Tier helpers (shared by pricing + withdrawal) ────────────────────────────
let tierSeq = 1;
const newTier = (): TierState => ({
  id: tierSeq++,
  from: "",
  feeType: "percentage",
  percentage: "",
  fixed: "",
  maxFee: "",
});

const tierFroms = (tiers: TierState[], firstFrom: string) =>
  tiers.map((t, i) => (i === 0 ? toNumber(firstFrom) : toNumber(t.from)));

const validateTiers = (
  tiers: TierState[],
  firstFrom: string,
  lastTo: string,
  requireMax: boolean
): string | null => {
  const froms = tierFroms(tiers, firstFrom);
  if (froms[0] <= 0) return "Enter a valid minimum amount";
  for (let i = 1; i < froms.length; i++) {
    if (froms[i] <= froms[i - 1])
      return `Tier ${i + 1} start must be greater than tier ${i} start`;
  }
  if (requireMax || lastTo !== "") {
    const max = toNumber(lastTo);
    if (max <= froms[froms.length - 1])
      return "Maximum amount must be greater than the last tier start";
  }
  for (let i = 0; i < tiers.length; i++) {
    const t = tiers[i];
    if (t.feeType === "percentage" || t.feeType === "percentage_plus_fixed") {
      const pct = toNumber(t.percentage);
      if (pct <= 0 || pct > 100)
        return `Tier ${i + 1}: enter a percentage between 0 and 100`;
    }
    if (t.feeType === "fixed" || t.feeType === "percentage_plus_fixed") {
      if (toNumber(t.fixed) <= 0)
        return `Tier ${i + 1}: enter a fixed amount greater than 0`;
    }
    if (t.maxFee !== "" && toNumber(t.maxFee) <= 0)
      return `Tier ${i + 1}: maximum fee must be greater than 0`;
  }
  return null;
};

const buildTiers = (tiers: TierState[], firstFrom: string, lastTo: string) => {
  const froms = tierFroms(tiers, firstFrom);
  return tiers.map((t, i) => ({
    from: froms[i],
    to:
      i < tiers.length - 1
        ? froms[i + 1] - 0.01
        : lastTo !== ""
          ? toNumber(lastTo)
          : null,
    feeType: t.feeType,
    percentage: t.feeType === "fixed" ? 0 : toNumber(t.percentage),
    fixed: t.feeType === "percentage" ? 0 : toNumber(t.fixed),
    maxFee: t.maxFee !== "" ? toNumber(t.maxFee) : null,
  }));
};

const mapTiersToState = (list: any[]): TierState[] =>
  list.map((t) => ({
    id: tierSeq++,
    from: t.from != null ? String(t.from) : "",
    feeType: (t.feeType as TierFeeType) || "percentage",
    percentage: t.percentage ? String(t.percentage) : "",
    fixed: t.fixed ? String(t.fixed) : "",
    maxFee: t.maxFee != null ? String(t.maxFee) : "",
  }));

// ── Reusable tier editor ─────────────────────────────────────────────────────
const TierEditor = ({
  tiers,
  setTiers,
  firstFrom,
  lastTo,
  lastToLabel = "—",
}: {
  tiers: TierState[];
  setTiers: React.Dispatch<React.SetStateAction<TierState[]>>;
  firstFrom: string;
  lastTo: string;
  lastToLabel?: string;
}) => {
  const fromOf = (i: number) => (i === 0 ? firstFrom : tiers[i].from);
  const toOf = (i: number): string => {
    if (i < tiers.length - 1) {
      const next = toNumber(fromOf(i + 1));
      return next > 0 ? formatAmountInput((next - 0.01).toFixed(2)) : "—";
    }
    return lastTo ? formatAmountInput(lastTo) : lastToLabel;
  };
  const updateTier = (i: number, patch: Partial<TierState>) =>
    setTiers((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const addTier = () => setTiers((prev) => [...prev, newTier()]);
  const removeTier = (i: number) =>
    setTiers((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <>
      <div className="space-y-4">
        {tiers.map((t, i) => (
          <div key={t.id} className="border-2 border-gray-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[13px] font-semibold text-[#0979A1]">
                Tier {i + 1}
              </span>
              {tiers.length > 1 && i > 0 && (
                <button
                  type="button"
                  onClick={() => removeTier(i)}
                  className="text-red-500 hover:text-red-600"
                  title="Remove tier">
                  <LuTrash2 size={16} />
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>From (₦)</label>
                <PlainAmountInput
                  value={fromOf(i)}
                  onChange={(v: string) => updateTier(i, { from: v })}
                  readOnly={i === 0}
                  placeholder={i === 0 ? "= minimum amount" : "Tier start"}
                  className={i === 0 ? roInputCls : inputCls}
                />
                {i === 0 && (
                  <p className="text-[11px] text-gray-400 mt-1">
                    Bound to the minimum amount.
                  </p>
                )}
              </div>
              <div>
                <label className={labelCls}>To (₦)</label>
                <input
                  readOnly
                  value={toOf(i)}
                  placeholder="—"
                  className={roInputCls}
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  {i < tiers.length - 1
                    ? "Derived from the next tier."
                    : "= maximum amount."}
                </p>
              </div>

              <div>
                <label className={labelCls}>Fee Type</label>
                <select
                  value={t.feeType}
                  onChange={(e) =>
                    updateTier(i, { feeType: e.target.value as TierFeeType })
                  }
                  style={selectChevron}
                  className="w-full h-[42px] pl-3 pr-9 border-2 rounded-md bg-white text-[14px] outline-none cursor-pointer appearance-none">
                  {TIER_FEE_TYPES.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Max Fee / Cap (₦)</label>
                <PlainAmountInput
                  value={t.maxFee}
                  onChange={(v: string) => updateTier(i, { maxFee: v })}
                  placeholder="Optional"
                  className={inputCls}
                />
              </div>

              {(t.feeType === "percentage" ||
                t.feeType === "percentage_plus_fixed") && (
                <div>
                  <label className={labelCls}>Percentage (%)</label>
                  <PlainAmountInput
                    value={t.percentage}
                    onChange={(v: string) => updateTier(i, { percentage: v })}
                    placeholder="e.g. 2.5"
                    className={inputCls}
                  />
                </div>
              )}
              {(t.feeType === "fixed" ||
                t.feeType === "percentage_plus_fixed") && (
                <div>
                  <label className={labelCls}>Fixed Amount (₦)</label>
                  <PlainAmountInput
                    value={t.fixed}
                    onChange={(v: string) => updateTier(i, { fixed: v })}
                    placeholder="e.g. 100"
                    className={inputCls}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addTier}
        className="flex items-center gap-1.5 text-[13px] font-medium text-[#0979A1] mt-4 hover:underline">
        <LuPlus size={15} /> Add tier
      </button>
    </>
  );
};

const FeeManagement = () => {
  const { makeRequest: getSettings } = useRequest("/settings", "GET");
  const { loading: feeLoading, makeRequest: setFee } = useRequest("/settings/fees", "POST");
  const { loading: limitLoading, makeRequest: setLimit } = useRequest("/settings/limit", "POST");

  // Tiered pricing / withdrawal endpoints (new, atomic full-config replace)
  const { makeRequest: getPricing } = useRequest("/settings/pricing", "GET");
  const { loading: savingPricing, makeRequest: savePricing } = useRequest("/settings/pricing", "PUT");
  const { makeRequest: getWithdrawal } = useRequest("/settings/withdrawal", "GET");
  const { loading: savingWithdrawal, makeRequest: saveWithdrawal } = useRequest("/settings/withdrawal", "PUT");

  const [existingFees, setExistingFees] = useState<any[]>([]);
  const [existingLimits, setExistingLimits] = useState<any[]>([]);

  const { handleSubmit: handleFeeSubmit, control: feeControl, reset: resetFee } = useForm<FeeFormData>({
    defaultValues: { feeType: "fixed", feeCurrency: "NGN", feeValue: "", setAsDefault: false },
  });

  const { handleSubmit: handleLimitSubmit, control: limitControl, reset: resetLimit } = useForm<LimitFormData>({
    defaultValues: { limitCurrency: "NGN", limitValue: "", limitType: "one_time" },
  });

  // ── Tiered pricing state ─────────────────────────────────────────────────
  const [minTxn, setMinTxn] = useState("");
  const [pricingTiers, setPricingTiers] = useState<TierState[]>([newTier()]);
  const [maxSelfService, setMaxSelfService] = useState("");
  const [aboveLabel, setAboveLabel] = useState("Contact Sales");

  // ── Tiered withdrawal state ──────────────────────────────────────────────
  const [minWithdrawal, setMinWithdrawal] = useState("");
  const [withdrawalTiers, setWithdrawalTiers] = useState<TierState[]>([newTier()]);
  const [maxWithdrawal, setMaxWithdrawal] = useState("");
  const [processingTime, setProcessingTime] = useState("60");

  const fetchSettings = async () => {
    const [response] = await getSettings();
    if (response?.data) {
      setExistingFees(response.data.fees || []);
      setExistingLimits(response.data.limits || []);
    }
  };

  const fetchTiered = async () => {
    const [pRes] = await getPricing(undefined, { currency: TIERED_CURRENCY });
    const p = pRes?.data;
    if (p) {
      setMinTxn(p.minTransactionAmount != null ? String(p.minTransactionAmount) : "");
      setAboveLabel(p.aboveMax?.label || "Contact Sales");
      if (Array.isArray(p.tiers) && p.tiers.length) {
        setPricingTiers(mapTiersToState(p.tiers));
        const last = p.tiers[p.tiers.length - 1];
        if (last?.to != null) setMaxSelfService(String(last.to));
      }
    }

    const [wRes] = await getWithdrawal(undefined, { currency: TIERED_CURRENCY });
    const w = wRes?.data;
    if (w) {
      setMinWithdrawal(w.minWithdrawal != null ? String(w.minWithdrawal) : "");
      setMaxWithdrawal(w.maxWithdrawal != null ? String(w.maxWithdrawal) : "");
      if (w.processingTimeMinutes != null) setProcessingTime(String(w.processingTimeMinutes));
      if (Array.isArray(w.feeTiers) && w.feeTiers.length) {
        setWithdrawalTiers(mapTiersToState(w.feeTiers));
      } else if (w.withdrawalFee != null) {
        // Legacy flat fee → seed a single fixed tier covering the whole range
        setWithdrawalTiers([
          { id: tierSeq++, from: "", feeType: "fixed", percentage: "", fixed: String(w.withdrawalFee), maxFee: "" },
        ]);
      }
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchTiered();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFeeSubmit = handleFeeSubmit(async (formData) => {
    const payload = { value: toNumber(formData.feeValue), type: formData.feeType, currency: formData.feeCurrency, setAsDefault: formData.setAsDefault };
    const [response, statusCode] = await setFee(payload);
    if (statusCode >= 200 && statusCode < 300) {
      showToast(response?.message || "Fee set successfully", true, { position: "top-center" });
      resetFee();
      fetchSettings();
    } else {
      showToast(response?.message || "Failed to set fee", false, { position: "top-center" });
    }
  });

  const onLimitSubmit = handleLimitSubmit(async (formData) => {
    const payload = { value: toNumber(formData.limitValue), type: formData.limitType, currency: formData.limitCurrency };
    const [response, statusCode] = await setLimit(payload);
    if (statusCode >= 200 && statusCode < 300) {
      showToast(response?.message || "Limit set successfully", true, { position: "top-center" });
      resetLimit();
      fetchSettings();
    } else {
      showToast(response?.message || "Failed to set limit", false, { position: "top-center" });
    }
  });

  const onSavePricing = async () => {
    const err = validateTiers(pricingTiers, minTxn, maxSelfService, true);
    if (err) return showToast(err, false, { position: "top-center" });
    const payload = {
      currency: TIERED_CURRENCY,
      minTransactionAmount: toNumber(minTxn),
      tiers: buildTiers(pricingTiers, minTxn, maxSelfService),
      aboveMax: { action: "contact_sales", label: aboveLabel || "Contact Sales" },
    };
    const [res, code] = await savePricing(payload);
    if (code >= 200 && code < 300)
      showToast(res?.message || "Pricing saved successfully", true, { position: "top-center" });
    else showToast(res?.message || "Failed to save pricing", false, { position: "top-center" });
  };

  const onSaveWithdrawal = async () => {
    const err = validateTiers(withdrawalTiers, minWithdrawal, maxWithdrawal, false);
    if (err) return showToast(err, false, { position: "top-center" });
    if (toNumber(processingTime) < 0)
      return showToast("Enter a valid processing time", false, { position: "top-center" });
    const payload = {
      currency: TIERED_CURRENCY,
      minWithdrawal: toNumber(minWithdrawal),
      maxWithdrawal: maxWithdrawal !== "" ? toNumber(maxWithdrawal) : null,
      processingTimeMinutes: toNumber(processingTime),
      feeTiers: buildTiers(withdrawalTiers, minWithdrawal, maxWithdrawal),
    };
    const [res, code] = await saveWithdrawal(payload);
    if (code >= 200 && code < 300)
      showToast(res?.message || "Withdrawal settings saved", true, { position: "top-center" });
    else showToast(res?.message || "Failed to save withdrawal settings", false, { position: "top-center" });
  };

  return (
    <>
      <div className='flex justify-between w-[95%]'>
        <section>
          <h2 className='text-[24px] font-bold'>
            Fee Management
            <p className='text-[14px] font-normal'>Manage withdrawal limits and transaction fees</p>
          </h2>
        </section>
        <section className='flex gap-4'>
          <Icon name='downloadIcon' />
          <Icon name='notificationIcon' />
        </section>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 w-[95%]'>
        {/* Withdrawal Limit Card */}
        <div className='bg-white border border-gray-200 rounded-xl p-6 shadow-sm'>
          <h3 className='text-[18px] font-semibold mb-1'>Withdrawal Limit</h3>
          <p className='text-[13px] text-gray-500 mb-5'>Set the maximum withdrawal limit per currency</p>
          <form onSubmit={onLimitSubmit}>
            <label className='text-[14px] font-normal text-[#040821] mb-2 block'>Type</label>
            <Controller
              name='limitType'
              control={limitControl}
              rules={{ required: true }}
              render={({ field }) => (
                <select {...field} style={selectChevron} className='w-full h-[45px] pl-3 pr-9 mb-4 border-2 rounded-md bg-white text-[14px] outline-none cursor-pointer appearance-none'>
                  <option value='one_time'>One Time</option>
                  <option value='daily'>Daily</option>
                </select>
              )}
            />
            <label className='text-[14px] font-normal text-[#040821] mb-2 block'>Amount</label>
            <div className='flex h-[45px] border-2 rounded-md overflow-hidden'>
              <Controller
                name='limitCurrency'
                control={limitControl}
                render={({ field }) => (
                  <select {...field} style={selectChevron} className='h-full pl-3 pr-9 bg-gray-50 border-r-2 text-[14px] outline-none cursor-pointer shrink-0 appearance-none'>
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}
              />
              <Controller
                name='limitValue'
                control={limitControl}
                rules={{ required: "Amount is required", validate: (v) => toNumber(v) > 0 || "Must be greater than 0" }}
                render={({ field }) => (
                  <AmountInput field={field} placeholder='Enter amount' className='flex-1 min-w-0 h-full px-4 outline-none bg-transparent text-[14px]' />
                )}
              />
            </div>
            {existingLimits.length > 0 && (
              <div className='mt-4'>
                <p className='text-[13px] font-medium text-gray-600 mb-2'>Current Limits</p>
                <div className='flex flex-wrap gap-2'>
                  {existingLimits.map((limit: any) => (
                    <span
                      key={limit.id}
                      className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1 rounded-full border ${currencyStyle(limit.currency)}`}>
                      <span className='font-semibold'>{limit.currency}</span>
                      <span className='px-1.5 py-[1px] rounded-full bg-white/70 text-[10px] font-medium uppercase tracking-wide'>
                        {limitTypeLabel(limit.limitType)}
                      </span>
                      <span className='font-medium'>{formatCount(limit.value)}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className='mt-6'>
              <Button size='sm' variant='primary' type='submit'>
                {limitLoading ? <CircleLoader color='#ffffff' loading={limitLoading} size={20} /> : "Set Limit"}
              </Button>
            </div>
          </form>
        </div>

        {/* Transaction Fee Card */}
        <div className='bg-white border border-gray-200 rounded-xl p-6 shadow-sm'>
          <h3 className='text-[18px] font-semibold mb-1'>Transaction Fee</h3>
          <p className='text-[13px] text-gray-500 mb-5'>Set the transaction fee by type and currency</p>
          <form onSubmit={onFeeSubmit}>
            <label className='text-[14px] font-normal text-[#040821] mb-2 block'>Fee</label>
            <div className='flex h-[45px] border-2 rounded-md overflow-hidden'>
              <Controller
                name='feeType'
                control={feeControl}
                render={({ field }) => (
                  <select {...field} style={selectChevron} className='h-full pl-2 pr-9 bg-gray-50 border-r-2 text-[13px] outline-none cursor-pointer capitalize shrink-0 appearance-none'>
                    <option value='fixed'>Fixed</option>
                    <option value='percentage'>Percentage</option>
                  </select>
                )}
              />
              <Controller
                name='feeValue'
                control={feeControl}
                rules={{ required: "Value is required", validate: (v) => toNumber(v) > 0 || "Must be greater than 0" }}
                render={({ field }) => (
                  <AmountInput field={field} placeholder='Enter value' className='flex-1 min-w-0 h-full px-3 outline-none bg-transparent text-[14px]' />
                )}
              />
              <Controller
                name='feeCurrency'
                control={feeControl}
                render={({ field }) => (
                  <select {...field} style={selectChevron} className='h-full pl-2 pr-9 bg-gray-50 border-l-2 text-[13px] outline-none cursor-pointer shrink-0 appearance-none'>
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}
              />
            </div>
            {existingFees.length > 0 && (
              <div className='mt-4'>
                <p className='text-[13px] font-medium text-gray-600 mb-2'>Current Fees</p>
                <div className='flex flex-wrap gap-2'>
                  {existingFees.map((fee: any) => (
                    <span
                      key={fee.id}
                      className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1 rounded-full border ${currencyStyle(fee.currency)}`}>
                      <span className='font-semibold'>{fee.currency}</span>
                      <span className='px-1.5 py-[1px] rounded-full bg-white/70 text-[10px] font-medium capitalize'>
                        {fee.feeType}
                      </span>
                      <span className='font-medium'>{formatCount(fee.value)}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <Controller
              name='setAsDefault'
              control={feeControl}
              render={({ field }) => (
                <label className='flex items-center gap-2 mt-4 cursor-pointer w-fit'>
                  <input
                    type='checkbox'
                    checked={field.value}
                    onChange={field.onChange}
                    className='w-4 h-4 accent-[#0979A1] cursor-pointer'
                  />
                  <span className='text-[14px] text-[#040821]'>Set as default</span>
                </label>
              )}
            />
            <div className='mt-6'>
              <Button size='sm' variant='primary' type='submit'>
                {feeLoading ? <CircleLoader color='#ffffff' loading={feeLoading} size={20} /> : "Set Fee"}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* ── Tiered configuration (NGN) ───────────────────────────────────── */}
      <div className='mt-10 w-[95%]'>
        <h3 className='text-[20px] font-bold'>Tiered Configuration</h3>
        <p className='text-[13px] text-gray-500'>
          Configure amount-based pricing and withdrawal tiers ({TIERED_CURRENCY})
        </p>
      </div>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-6 mt-4 w-[95%] items-start'>
        {/* Transaction Pricing (tiered) */}
        <div className='bg-white border border-gray-200 rounded-xl p-6 shadow-sm'>
          <h3 className='text-[18px] font-semibold mb-1'>Transaction Pricing</h3>
          <p className='text-[13px] text-gray-500 mb-5'>
            Set the minimum transaction amount and pricing tiers. Ranges are kept
            contiguous automatically.
          </p>

          <div className='mb-5 max-w-[240px]'>
            <label className={labelCls}>Minimum Transaction Amount (₦)</label>
            <PlainAmountInput
              value={minTxn}
              onChange={setMinTxn}
              placeholder='e.g. 3,000'
              className={inputCls}
            />
            <p className='text-[11px] text-gray-400 mt-1'>
              Transactions below this amount are not allowed.
            </p>
          </div>

          <TierEditor
            tiers={pricingTiers}
            setTiers={setPricingTiers}
            firstFrom={minTxn}
            lastTo={maxSelfService}
          />

          <div className='mt-5 border-t border-gray-100 pt-5 grid grid-cols-2 gap-3'>
            <div>
              <label className={labelCls}>Maximum Self-Service Amount (₦)</label>
              <PlainAmountInput
                value={maxSelfService}
                onChange={setMaxSelfService}
                placeholder='e.g. 10,000,000'
                className={inputCls}
              />
              <p className='text-[11px] text-gray-400 mt-1'>
                Caps the last tier. Amounts above this fall to the action below.
              </p>
            </div>
            <div>
              <label className={labelCls}>Above Maximum — Action</label>
              <input
                value={aboveLabel}
                onChange={(e) => setAboveLabel(e.target.value)}
                placeholder='Contact Sales'
                className={inputCls}
              />
              <p className='text-[11px] text-gray-400 mt-1'>
                Shown to users for amounts above the maximum.
              </p>
            </div>
          </div>

          <div className='mt-6'>
            <Button size='sm' variant='primary' type='button' onClick={onSavePricing}>
              {savingPricing ? <CircleLoader color='#ffffff' loading size={20} /> : "Save Pricing"}
            </Button>
          </div>
        </div>

        {/* Withdrawal Settings (tiered) */}
        <div className='bg-white border border-gray-200 rounded-xl p-6 shadow-sm'>
          <h3 className='text-[18px] font-semibold mb-1'>Withdrawal Settings</h3>
          <p className='text-[13px] text-gray-500 mb-5'>
            Configure amount, processing time and withdrawal fee tiers.
          </p>

          <div className='mb-5 max-w-[240px]'>
            <label className={labelCls}>Minimum Withdrawal Amount (₦)</label>
            <PlainAmountInput
              value={minWithdrawal}
              onChange={setMinWithdrawal}
              placeholder='Enter amount'
              className={inputCls}
            />
          </div>

          <TierEditor
            tiers={withdrawalTiers}
            setTiers={setWithdrawalTiers}
            firstFrom={minWithdrawal}
            lastTo={maxWithdrawal}
            lastToLabel='No limit'
          />

          <div className='mt-5 border-t border-gray-100 pt-5 grid grid-cols-2 gap-3'>
            <div>
              <label className={labelCls}>Maximum Withdrawal Amount (₦)</label>
              <PlainAmountInput
                value={maxWithdrawal}
                onChange={setMaxWithdrawal}
                placeholder='Optional — blank for no limit'
                className={inputCls}
              />
              <p className='text-[11px] text-gray-400 mt-1'>
                Caps the last tier. Leave blank for no upper limit.
              </p>
            </div>
            <div>
              <label className={labelCls}>Processing Time (minutes)</label>
              <PlainAmountInput
                value={processingTime}
                onChange={setProcessingTime}
                placeholder='e.g. 60'
                className={inputCls}
              />
              <p className='text-[11px] text-gray-400 mt-1'>
                Currently 60 minutes (1 hour).
              </p>
            </div>
          </div>

          <div className='mt-6'>
            <Button size='sm' variant='primary' type='button' onClick={onSaveWithdrawal}>
              {savingWithdrawal ? <CircleLoader color='#ffffff' loading size={20} /> : "Save Withdrawal Settings"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FeeManagement;
